import {useCallback, useEffect, useMemo} from 'react'
import {SetURLSearchParams, useSearchParams} from 'react-router'
import {useLocalStorage} from 'react-use'

import {
  combineSearchParamsToQuery,
  filterSearchParams,
  pickKeysFromSearchParams,
  sameSearchParamsByKeys
} from './queryParameter.utils'

type Options = {
  keepInLocalStorage?: boolean
}

/**
 * Hook for managing URL query parameters with optional localStorage persistence.
 *
 * Synchronizes specified query param keys between the URL and localStorage.
 * On mount, restores params from localStorage if not present in URL.
 * When `keepInLocalStorage` is enabled, persists changes to localStorage automatically.
 *
 * @param id - Unique identifier used as the localStorage key
 * @param keys - Array of query parameter keys to manage
 * @param options - Configuration options
 * @param options.keepInLocalStorage - Whether to persist values to localStorage
 * @returns Tuple of [current value as query string, setter function]
 */
export const useQueryParamValue = (
  id: string,
  keys: string[],
  options: Options
): [string | undefined, (value: string | undefined) => void] => {
  const [storageData, setStorageData, removeStorageData] = useLocalStorage<string>(id)
  const [params, setParams]: [URLSearchParams, SetURLSearchParams] = useSearchParams()
  const hasParamValue = keys.some((key) => params.has(key))
  const {keepInLocalStorage} = options

  useEffect(() => {
    if (storageData && !hasParamValue) {
      const currentParams = new URLSearchParams(params)
      const newParams = combineSearchParamsToQuery(
        currentParams,
        pickKeysFromSearchParams(new URLSearchParams(storageData), keys)
      )
      setParams(newParams, {replace: true, flushSync: true})
    }
    if (
      keepInLocalStorage &&
      hasParamValue &&
      !sameSearchParamsByKeys(params, new URLSearchParams(storageData), keys)
    ) {
      setStorageData(pickKeysFromSearchParams(params, keys).toString())
    }
  }, [storageData, setParams, setStorageData, params, keepInLocalStorage, hasParamValue, keys])

  const setQueryParam = useCallback(
    (value: string | undefined) => {
      const currentParams = filterSearchParams(params, keys)
      const newParams = combineSearchParamsToQuery(currentParams, new URLSearchParams(value))
      if (keepInLocalStorage) {
        if (value) {
          setStorageData(value)
        } else {
          removeStorageData()
        }
      }
      setParams(newParams, {replace: true})
    },
    [keepInLocalStorage, keys, params, removeStorageData, setParams, setStorageData]
  )

  const value = useMemo<string | undefined>(
    () => pickKeysFromSearchParams(params, keys).toString() || storageData,
    [keys, params, storageData]
  )

  return [value, setQueryParam]
}
